import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import { Spinner } from '../components/Spinner';
import { listClients } from '../lib/catalogue';
import { usePaged } from '../lib/hooks';

export function ClientListPage() {
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const [input, setInput] = useState(search);
  const removed = (useLocation().state as { removed?: string } | null)?.removed;

  // Debounce typing into the ?q= parameter, which drives the query.
  useEffect(() => {
    const t = setTimeout(() => {
      const q = input.trim();
      if (q !== search) setParams(q ? { q } : {}, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [input, search, setParams]);

  const load = useCallback((after: Parameters<typeof listClients>[1]) => listClients(search, after), [search]);
  const { loading, items, hasMore, error, loadingMore, loadMore } = usePaged(search, load);

  return (
    <section>
      <div className="page-head">
        <h1>Clients</h1>
        <Link to="/clients/new" className="btn btn--primary">
          Add client
        </Link>
      </div>

      <div className="toolbar">
        <label htmlFor="search" className="visually-hidden">
          Search by last name
        </label>
        <input
          id="search"
          type="search"
          className="search"
          placeholder="Search by last name…"
          autoComplete="off"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
      </div>

      {removed && (
        <p className="alert alert--success" role="status">
          Removed {removed} and their vehicles.
        </p>
      )}

      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <Spinner />
      ) : items.length === 0 && !error ? (
        <p className="empty">
          {search ? `No clients with a last name starting "${search}".` : 'No clients yet. Add the first one.'}
        </p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Phone</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/clients/${c.id}`}>
                      {c.lastName}, {c.firstName}
                    </Link>
                  </td>
                  <td>
                    <a href={`mailto:${c.email}`}>{c.email}</a>
                  </td>
                  <td>
                    <a href={`tel:${c.phone}`}>{c.phone}</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && (
        <div className="load-more">
          <button type="button" className="btn" onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}
